import { LoadingSpinner, Header, Button } from '@components/Generic'
import { GocardlessRedirectingScreen } from '@components/Gocardless/GocardlessScreens'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@methods/devLog'
import { axiosInstance } from '@methods/axiosConfig'
import { forceRedirect, withCommonQueryParams } from '@methods/forceRedirect'
import { IBAN_CONFIRMATION_PATH, INCOME_CONFIRMATION_PATH } from '@methods/gocardless'
import { useInterval } from 'usehooks-ts'
import { API_URL, GOCARDLESS_SERVICE_PATH } from '@constants/apiUrl'
import {
  forgetGocardlessSessionToken,
  withGocardlessSessionToken,
} from '@methods/gocardlessSessionToken'

// POST complete answers with this once the session is processed. `incomeConfirmationRequired` is set
// when the account transactions report could not settle the user's income and the flow asked to have
// the user confirm it: the transaction then waits in `awaitingIncomeConfirmation` and must not be
// handed back to the client before the confirmation screen answered. `ibanSupplementRequired` is set
// when the bank returned an account number but no IBAN and the tenant asked to have it supplemented:
// the transaction waits in `awaitingIbanConfirmation` for the IBAN screens (which may send the user
// on to the income confirmation afterwards).
interface CompleteResponse {
  incomeConfirmationRequired?: boolean
  ibanSupplementRequired?: boolean
}

export const GocardlessProcessingPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessProcessingPage: trans } = store.TranslationsState.translations
  const [isComplete, setIsComplete] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const checkStatus = async (): Promise<void> => {
    try {
      const res = await axiosInstance.post<CompleteResponse | undefined>(
        withGocardlessSessionToken(`${API_URL}/${GOCARDLESS_SERVICE_PATH}/complete`),
        {},
        {
          withCredentials: true,
          // A 4xx here still means the session can be handed back to the
          // backend: `redirect-back` decides where the user goes, including
          // when the transaction was already processed by an earlier poll.
          // The two authorization codes are the exception — with neither a
          // cookie nor a valid token, `redirect-back` would answer with a raw
          // 401 and strand the user, so let them reject and have the axios
          // interceptor route to `session-unauthorized`. sc-21124
          validateStatus: (status) =>
            status === 200 || (status >= 400 && status < 500 && status !== 401 && status !== 403),
        }
      )

      // Authorized by cookie or token, the backend answered — and on the token
      // path it re-issued the cookie with this response, so the browser now
      // carries the session on its own and the stored token is only exposure.
      // Anything unauthorized rejects above instead of reaching this line, so
      // the token is never dropped while it is still the only way in. sc-21124
      forgetGocardlessSessionToken()

      // Only a 200 carries the flags; the accepted 4xx bodies are error payloads.
      if (res.status === 200 && res.data?.ibanSupplementRequired) {
        goToScreen(IBAN_CONFIRMATION_PATH)
        return
      }

      if (res.status === 200 && res.data?.incomeConfirmationRequired) {
        goToScreen(INCOME_CONFIRMATION_PATH)
        return
      }

      setIsComplete(true)
    } catch (e) {
      devLog(e)
    }
  }

  // Same web origin, same browser: the cookie POST complete re-issued carries the session on,
  // and language and themeId travel in the URL like they did on the return page.
  const goToScreen = (path: string): void => {
    setIsComplete(true)
    setIsRedirecting(true)

    forceRedirect(withCommonQueryParams(path))
  }

  const redirectToNextPage = async (): Promise<void> => {
    try {
      setIsRedirecting(true)

      forceRedirect(
        withGocardlessSessionToken(`${API_URL}/${GOCARDLESS_SERVICE_PATH}/redirect-back`)
      )
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    checkStatus()
  }, [])

  useInterval(checkStatus, !isComplete ? 3000 : null)

  if (isComplete && isRedirecting) {
    return (
      <GocardlessRedirectingScreen
        header={trans?.redirectingHeader}
        label={trans?.redirectingLabel}
        buttonLabel={trans?.redirectingButtonLabel}
      />
    )
  }

  if (isComplete) {
    return (
      <div className="page-container mobile-m-0">
        <Header>{trans?.readyHeader}</Header>

        <p className="generic__paragraph-centered">{trans?.readyLabel}</p>

        <div className="layout__content-container-bottom-centered">
          <Button onClick={redirectToNextPage} className="button--w164">
            {trans?.proceed}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <Header>{trans?.header}</Header>

      <p className="generic__paragraph-centered">{trans?.label}</p>

      <LoadingSpinner className="loading-spinner--small-padding" />
    </div>
  )
})
